import React, { useState, useCallback, useEffect, useMemo } from 'react';

import { Column } from 'primereact/column';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { addMonths } from 'date-fns';

import { Container } from './styles';

import OrcamentoService from '../../services/OrcamentoService';
import AuthService from '../../services/AuthService';
import history from '../../services/history';
import {
  getPageParams,
  errorHandle,
  calcNaxItemsPage,
  isScreenMobile,
  formatDate,
  padLeft,
} from '../../util/functions';

import { showMessage } from '../../components/MessageDialog';

import BotaoMenuGrid from '../../components/BotaoMenuGrid';

import OrcamentoCrud from './crud';
import { StateScreen } from '../constants';
import CrudHeader from '../../components/CrudHeader';
import DataTableSp from '../../components/DataTableSp';
import LabelSp from '../../components/LabelSp';
import RevendaService from '../../services/RevendaService';
import DropdownSp from '../../components/DropdownSp';
import { cfgPtBr } from '../../config/Constantes';
import CalendarSp from '../../components/CalendarSp';
import ImgPdfView from '../../components/ImgPdfView';

export default function Orcamento(props) {
  // useSelectors
  const filterVisible = useSelector(state => state.global.filterVisible);

  // useMemo
  const pageParams = useMemo(() => getPageParams(props.match.params, props.location.search), [
    props.location.search,
    props.match.params,
  ]);

  const filterService = useMemo(() => {
    const f = OrcamentoService.getFilter();
    f.idRevenda = AuthService.getUsuario().idRevenda;
    return f;
  }, []);

  // useStates
  const toBack = pageParams.toBack || '/orcamentos';

  const [filter, setFilter] = useState(filterService);
  const [orcamentos, setOrcamento] = useState([]);

  const [pageLimit, setPageLimit] = useState(filterService.limit);
  const [first, setFirst] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSelected, setPageSelected] = useState(0);
  const [revendas, setRevendas] = useState();
  const [funcTimeOut, setFuncTimeOut] = useState();
  const [showPdf, setShowPdf] = useState(false);
  const [pdfSrc, setPdfSrc] = useState();

  // useCallbacks

  const calcLimit = useCallback(() => {
    let ret;
    if (filterVisible) {
      ret = calcNaxItemsPage(8, 12, 17);
    } else {
      ret = calcNaxItemsPage(13, 17, 22);
    }
    setPageLimit(ret);
    return ret;
  }, [filterVisible]);

  const loadRevenda = useCallback(async () => {
    const r = await RevendaService.findAll({
      limit: 999,
    });

    const retorno = r.items.map(e => {
      return {
        label: e.nome,
        value: e.id,
      };
    });

    setRevendas(retorno);
  }, []);

  const handleBuscar = useCallback(
    async (_filter, _page, resetPage = true) => {
      _filter.page = _page || 0;
      _filter.limit = calcLimit();

      try {
        const result = await OrcamentoService.findAll({ ..._filter, resumo: 'resumo' });
        setOrcamento(result.items);

        setTotalRecords(result.totalRecords);
        if (resetPage) {
          setFirst(0);
        }
      } catch (err) {
        errorHandle(err);
      }
    },
    [calcLimit]
  );

  const setFilterAndSearch = useCallback(
    async _filterValue => {
      if (JSON.stringify(_filterValue) !== JSON.stringify(filter)) {
        setFilter(_filterValue);

        if (funcTimeOut) {
          clearTimeout(funcTimeOut);
        }
        const func = setTimeout(async () => {
          handleBuscar(_filterValue);
        }, 800);
        setFuncTimeOut(func);
      }
    },
    [filter, funcTimeOut, handleBuscar]
  );

  function onPage(event) {
    const pagina = event.first / event.rows;
    setPageSelected(pagina);
    setFirst(event.first);
    handleBuscar(filter, pagina, false);
  }

  // useCallbacks
  const excluirRegistro = useCallback(
    async _id => {
      try {
        await OrcamentoService.delete(_id);
        toast.success('Registro excluído com sucesso.');
        handleBuscar(filter);
      } catch (err) {
        errorHandle(err);
      }
    },
    [filter, handleBuscar]
  );

  // functions
  function getTitle() {
    const titleDefault = 'Orçamento';
    let titleAdd = '';

    if (pageParams.stateScreen === StateScreen.stSearch) {
      titleAdd = '';
    }
    if (pageParams.stateScreen === StateScreen.stInsert) {
      titleAdd = '(Novo)';
    }
    if (pageParams.stateScreen === StateScreen.stUpdate) {
      titleAdd = `(Alterando Id: ${pageParams.idSelected})`;
    }
    if (pageParams.stateScreen === StateScreen.stView) {
      titleAdd = ` (Visualizando Id: ${pageParams.idSelected})`;
    }

    if (!isScreenMobile()) {
      return `${titleDefault} ${titleAdd}`;
    }

    return titleDefault;
  }

  const confirmaExclusao = useCallback(
    id => {
      showMessage('Confirmação', 'Confirma a exclusão do registro?', idx => {
        if (idx === 1) {
          excluirRegistro(id);
        }
      });
    },
    [excluirRegistro]
  );

  const confirmaPrint = async id => {
    showMessage('Confirmação', 'Deseja imprimir apenas módulos contratados?', idx => {
      if (idx === 1) {
        print(id, true);
      } else {
        print(id, false);
      }
    });
  };
  const print = async (id, exibirApenasModulosContratados) => {
    try {
      const retorno = await OrcamentoService.getReport(id, exibirApenasModulosContratados);
      const srcPdf = URL.createObjectURL(retorno);
      setPdfSrc(srcPdf);
      setShowPdf(true);
    } catch (err) {
      errorHandle(err);
    }
  };

  function bloqueiaRevenda() {
    return (
      AuthService.getUsuario() &&
      AuthService.getUsuario().idRevenda &&
      !AuthService.isPerfilGerente()
    );
  }

  // useEffects
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      handleBuscar(filter, 0, true);
    }
    loadRevenda();
    // desativado para evitar que a cada vez que o cliente digitasse o sistema buscasse
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffects
  // Responsavel por recalcular a quantidade de registros da grid quando a div do filtro ocultar
  // ou ficar visivel
  useEffect(() => {
    if (pageParams.stateScreen === StateScreen.stSearch) {
      calcLimit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calcLimit]);

  // renders
  return (
    <Container className="container-page page-enter-tab">
      <div className="p-grid">
        <CrudHeader
          title={getTitle()}
          disabledButtonInsert={!AuthService.checkRoles('ORCAMENTO_INSERIR')}
          showButtonFilter={pageParams.stateScreen === StateScreen.stSearch}
          showButtonInsert
          showButtonSearch
          titleFilterDefault="Buscar por Nome"
          handleFilterDefault={text => {
            filter.nome = text;
            setFilter({ ...filter, nome: text });
            handleBuscar(filter);
          }}
          handleClearFilters={() => {
            setFilter({
              ...filter,
              nome: '',
              idRevenda: null,

              dataInicio: addMonths(new Date(), -12 * 10),
              dataFim: new Date(),
            });
          }}
          handleButtonInsert={() => history.push('/orcamentos/insert')}
          handleButtonSearch={() => handleBuscar(filter)}
        >
          <div
            className="p-grid p-col-12 p-sm-6 p-lg-6 p-fluid"
            style={{ margin: 0, padding: 0 }}
          >
            <div className="p-col-6 p-fluid">
              <LabelSp htmlFor="dataInicial">De: </LabelSp>
              <CalendarSp
                showIcon
                autoFocus
                showOnFocus={false}
                appendTo={document.body}
                dateFormat="dd/mm/yy"
                yearNavigator
                locale={cfgPtBr}
                id="dataInicial"
                value={filter.dataInicio}
                yearRange="2010:2040"
                onChange={e => {
                  setFilterAndSearch({ ...filter, dataInicio: e.value });
                }}
              />
            </div>
            <div className="p-col-6 p-fluid">
              <LabelSp htmlFor="dataFinal">Até: </LabelSp>
              <CalendarSp
                appendTo={document.body}
                showIcon
                showOnFocus={false}
                locale={cfgPtBr}
                id="dataFinal"
                dateFormat="dd/mm/yy"
                value={filter.dataFim}
                yearNavigator
                yearRange="2010:2040"
                onChange={e => setFilterAndSearch({ ...filter, dataFim: e.value })}
              />
            </div>
          </div>

          <div className="p-col-12 p-sm-6 p-lg-6 p-fluid">
            <LabelSp>Revenda</LabelSp>
            <DropdownSp
              placeholder="Todos"
              showClear
              disabled={bloqueiaRevenda()}
              onChange={e => {
                setFilterAndSearch({ ...filter, idRevenda: e?.value });
              }}
              options={revendas}
              value={filter.idRevenda}
            />
          </div>
        </CrudHeader>
        {pageParams.stateScreen === StateScreen.stSearch ? renderSearch() : renderCrud()}
      </div>
    </Container>
  );

  function renderSearch() {
    return (
      <>
        {showPdf ? (
          <ImgPdfView
            title="Relatório"
            src={pdfSrc}
            visible={showPdf}
            setVisible={() => {
              setPdfSrc(undefined);
              setShowPdf(false);
            }}
          />
        ) : null}
        <div className="p-col-12 p-fluid datatable-doc-demo">
          <DataTableSp
            value={orcamentos}
            style={{ marginBottom: '2px' }}
            paginator
            rows={pageLimit}
            lazy
            responsive
            totalRecords={totalRecords}
            first={first}
            onPage={onPage}
          >
            <Column
              field="id"
              header="Id"
              className="grid-col-id"
              style={{ width: 65 }}
              body={rowData => padLeft(rowData.id, 6)}
            />
            <Column
              field="dataLancamento"
              className="grid-col grid-col-data"
              header="Data"
              body={rowData => formatDate(rowData.dataLancamento, 'dd/MM/yyyy')}
            />
            <Column
              field="nome"
              className="grid-col grid-col-nowrap"
              header="Nome"
              body={rowData => <span title={rowData.nome}>{rowData.nome}</span>}
            />
            <Column
              field="contato"
              className="grid-col grid-col-nowrap"
              style={{ width: 150 }}
              header="Contato"
              body={rowData => <span title={rowData.contato}>{rowData.contato}</span>}
            />
            <Column
              field="cpfCnpj"
              className="grid-col p-p-5"
              style={{ width: 133 }}
              header="CPF/CNPJ"
            />
            <Column
              field="fone"
              className="grid-col"
              style={{ width: 103 }}
              header="Telefone"
            />

            <Column
              field="dataValidade"
              className="grid-col grid-col-data p-p-4"
              header="Validade"
              body={rowData => formatDate(rowData.dataValidade, 'dd/MM/yyyy')}
            />

            <Column
              className="gid-col-acoes-35"
              bodyStyle={{ textAlign: 'end' }}
              body={renderButtonOp}
            />
          </DataTableSp>
        </div>
      </>
    );
  }

  function renderButtonOp(rowData) {
    return (
      <BotaoMenuGrid
        labels={['Visualizar', 'Alterar', 'Excluir', 'Imprimir']}
        icons={['pi pi-search', 'pi pi-pencil', 'pi pi-trash', 'pi pi-print']}
        handles={[
          () => history.push(`/orcamentos/${rowData.id}?view`),
          () => history.push(`/orcamentos/${rowData.id}`),
          () => confirmaExclusao(rowData.id),
          () => confirmaPrint(rowData.id),
        ]}
        disableds={[
          false,
          !AuthService.checkRoles('ORCAMENTO_ALTERAR'),
          !AuthService.checkRoles('ORCAMENTO_EXCLUIR'),
          !AuthService.checkRoles('ORCAMENTO_IMPRIMIR'),
        ]}
      />
    );
  }

  function renderCrud() {
    return (
      <OrcamentoCrud
        idSelected={pageParams.idSelected}
        stateScreen={pageParams.stateScreen}
        onClose={() => {
          history.push(toBack);
          handleBuscar(filter, pageSelected, false);
        }}
      />
    );
  }
}
